import React, { useEffect, useState } from "react";
import { apiConfigured, authOptions, signOut, whoami, type Account, type AuthOptions } from "../lib/api";
import { wantsAccount } from "../lib/entry";
import { AuthPanel, AuthSettings } from "./AuthPanel";
import { bannerFor, resetTokenFrom, urlWithoutAuthParams, type Banner } from "../lib/authui";

/**
 * The account control in the top bar.
 *
 * It renders nothing at all when no API is configured, which is the default
 * and the state the console was designed around. Signing in is additive.
 */
export function AccountBar() {
  const [account, setAccount] = useState<Account>({ authenticated: false });
  const [opts, setOpts] = useState<AuthOptions | null>(null);
  const [busy, setBusy] = useState(false);
  /*
      Opened deliberately, never mounted over the console. Invariant 13: an
      account is additive, so the auth UI must be something a person chooses to
      look at, not something standing between them and their wallet.
      It opens by itself only when the URL carries a reset token or the landing
      page's ?account=1 hint -- both mean the person arrived here to do this.
  */
  /*
      THE URL IS READ EXACTLY ONCE, HERE, AND THEN ERASED.

      This used to live inside AuthPanel, and that was wrong in a way only a
      browser could show: clicking a verification link signs you in, so the
      signed-in branch renders and AuthPanel never mounts -- leaving
      "?verified=1" in the address bar and the confirmation message unshown.
      The URL is global state, so the component that is always mounted owns it.
      A useState initialiser runs before any effect, so the value is captured
      before the effect below wipes it.
  */
  const [arrival] = useState(() => ({
    banner: bannerFor(window.location.search),
    resetToken: resetTokenFrom(window.location.search),
    wanted: wantsAccount(window.location.search),
  }));
  const [banner, setBanner] = useState<Banner>(arrival.banner);
  const [panel, setPanel] = useState<boolean>(arrival.resetToken !== null || arrival.wanted);
  const [settings, setSettings] = useState(false);

  useEffect(() => {
    // A reset token in the address bar is a credential in the address bar: it
    // reaches referrers, history, and whatever syncs that history. Erase it the
    // moment it has been read.
    window.history.replaceState(null, "", urlWithoutAuthParams(window.location.href));
  }, []);

  useEffect(() => {
    if (!apiConfigured) return;
    let live = true;
    void whoami().then((a) => live && setAccount(a));
    void authOptions().then((o) => live && setOpts(o));
    return () => {
      live = false;
    };
  }, []);

  if (!apiConfigured) return null;

  if (account.authenticated && account.user) {
    const who = account.user.name ?? account.user.email ?? "Signed in";
    return (
      <div className="row" data-testid="account-bar">
        <span className="pill ok" title={account.org?.name ?? ""} data-testid="account-role">
          {account.org?.role ?? "member"}
        </span>
        <span style={{ fontSize: 12, color: "var(--muted)" }} data-testid="account-who">
          {who}
        </span>
        <button className="ghost" data-testid="btn-settings" onClick={() => setSettings((v) => !v)}>
          Account
        </button>
        <button
          className="ghost"
          disabled={busy}
          data-testid="btn-signout"
          onClick={async () => {
            setBusy(true);
            await signOut();
            setAccount({ authenticated: false });
            setBusy(false);
          }}
        >
          Sign out
        </button>
        {banner ? (
          <span className={`auth-note ${banner.kind}`} data-testid="auth-banner">{banner.text}</span>
        ) : null}
        {settings ? (
          <div className="auth-pop">
            <AuthSettings email={account.user.email} hasPassword={true} />
          </div>
        ) : null}
      </div>
    );
  }

  /*
      "No OAuth providers" stopped meaning "no accounts" the day password
      signup arrived. A deployment with no Google client but a working mail
      provider offers accounts perfectly well, and this branch used to hide the
      entire sign-in UI from it -- observed live: the panel never rendered and
      the bar just said "Accounts unavailable".
  */
  const anyWay = Boolean(opts && (opts.providers.length > 0 || opts.password));
  if (opts && !anyWay) {
    return (
      <span style={{ fontSize: 12, color: "var(--faint)" }} data-testid="account-bar">
        Accounts unavailable
      </span>
    );
  }
  if (!opts) return null;

  /*
      `?account=1` is the landing page saying "this person clicked Create an
      account". It HIGHLIGHTS the control that is already here; it does not
      hide a panel, block the wallet, or change what an anonymous visitor can
      do. Invariant 13 -- signing in is additive, and a hint that could refuse
      anything would stop being a hint.
  */
  const hinted = wantsAccount(window.location.search) || panel;
  return (
    <div
      className={hinted ? "row account-hint" : "row"}
      data-testid="account-bar"
      data-hinted={hinted ? "1" : undefined}
    >
      <button data-testid="btn-open-auth" onClick={() => setPanel((v) => !v)}>
        {panel ? "Close" : "Sign in"}
      </button>
      {banner ? (
        <span className={`auth-note ${banner.kind}`} data-testid="auth-banner">{banner.text}</span>
      ) : null}
      {panel ? (
        <div className="auth-pop">
          <AuthPanel
            resetToken={arrival.resetToken}
            onDone={() => {
              setPanel(false);
              setBanner(null);
              void whoami().then(setAccount);
            }}
          />
        </div>
      ) : null}
    </div>
  );
}

/**
 * The console never asks for a password, and a wallet tool is exactly what a
 * lookalike clones. Saying so next to the sign-in button is cheap and is the
 * only defence a user actually has.
 */
export function AccountNote() {
  if (!apiConfigured) return null;
  return (
    <div className="note" style={{ marginTop: 12 }}>
      <p>
        Signing in is optional and only stores your rehearsals and an audit trail. It never touches
        your wallet, never holds a key, and is not required to launch anything. oxMinter will never
        ask for a password or a seed phrase — check the address in your browser bar before you
        approve any sign-in.
      </p>
    </div>
  );
}
